import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CircularProgress from '@mui/material/CircularProgress';

import ContentEntryModal from '@/components/workflow/marketing/ContentEntryModal';
import MarketingTodoList from '@/components/workflow/marketing/MarketingTodoList';
import { getEntries, getMarketingStaff } from '@/services/marketingCalendarService';
import { getDefaultAuthenticatedHomePath, isMarketingUser } from '@/services/adminAuthority';

const ACCENT = '#c2410c';
const DENIED_MESSAGE = 'Marketing calendar access required';
const REDIRECT_SECONDS = 4;
const DONE = '#16a34a';

const todayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MarketingCalendar = () => {
  const navigate = useNavigate();
  const calRef = useRef(null);
  const [access, setAccess] = useState(() => (isMarketingUser() ? 'checking' : 'denied'));
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [entries, setEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async (from, to) => {
    if (!from || !to) return;
    setLoading(true);
    try {
      setEntries(await getEntries(from, to));
      setError(null);
    } catch (e) {
      if (e.message === DENIED_MESSAGE) setAccess('denied');
      else setError(e.message);
      setEntries([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    getMarketingStaff()
      .then((s) => { setStaff(s || []); setAccess('ok'); })
      .catch((e) => {
        if (e.message === DENIED_MESSAGE) setAccess('denied');
        else { setStaff([]); setAccess('ok'); }
      });
  }, []);

  useEffect(() => {
    if (access === 'ok' && range) load(range.from, range.to);
  }, [access, range, load]);

  useEffect(() => {
    if (access !== 'denied') return;
    const home = getDefaultAuthenticatedHomePath();
    const tick = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    const go = setTimeout(() => navigate(home, { replace: true }), REDIRECT_SECONDS * 1000);
    return () => { clearInterval(tick); clearTimeout(go); };
  }, [access, navigate]);

  const refresh = () => range && load(range.from, range.to);

  const events = entries.map((e) => ({
    id: String(e.id),
    title: e.title,
    start: e.due_date,
    allDay: true,
    backgroundColor: e.is_completed ? DONE : (e.assignees?.[0]?.color || ACCENT),
    borderColor: e.is_completed ? DONE : (e.assignees?.[0]?.color || ACCENT),
    extendedProps: { entry: e },
  }));

  if (access === 'checking') {
    return (
      <div className="h-full flex items-center justify-center">
        <CircularProgress size={28} style={{ color: ACCENT }} />
      </div>
    );
  }

  if (access === 'denied') {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center bg-white rounded-2xl border border-gray-200 shadow-sm px-8 py-10">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
            <LockOutlinedIcon style={{ fontSize: 24 }} className="text-amber-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">No access to the Marketing Calendar</h2>
          <p className="text-sm text-gray-600 mt-2">
            Ask a PH Admin to tick you under <span className="font-semibold">Group Managers → Roster → Marketing</span>.
          </p>
          <p className="text-xs text-gray-400 mt-4">
            Taking you back{countdown > 0 ? ` in ${countdown}…` : '…'}
          </p>
          <button
            type="button"
            onClick={() => navigate(getDefaultAuthenticatedHomePath(), { replace: true })}
            className="mt-5 px-4 py-2 rounded-lg text-white text-sm font-semibold"
            style={{ backgroundColor: ACCENT }}
          >
            Go back now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col p-4">
      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <h1 className="text-xl font-bold text-gray-900">Marketing Content Calendar</h1>
        {loading && <span className="text-xs text-gray-400">Loading…</span>}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setModal({ mode: 'create', defaultDate: todayYMD() })}
          className="px-4 py-2 rounded-lg text-white text-sm font-semibold"
          style={{ backgroundColor: ACCENT }}
        >
          + New content
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex-shrink-0">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 flex gap-3 overflow-hidden">
        <aside className="hidden lg:flex flex-col w-64 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex-1 min-h-0 overflow-y-auto">
            <MarketingTodoList />
          </div>
        </aside>

        <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-200 p-3 overflow-hidden marketing-calendar">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          height="100%"
          firstDay={1}
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,listMonth' }}
          events={events}
          datesSet={(info) => {
            const from = info.startStr.slice(0, 10);
            const to = info.endStr.slice(0, 10);
            setRange((r) => (r && r.from === from && r.to === to ? r : { from, to }));
          }}
          dateClick={(info) => setModal({ mode: 'create', defaultDate: info.dateStr })}
          eventClick={(info) => setModal({ mode: 'edit', entryId: Number(info.event.id) })}
          eventContent={(arg) => {
            const e = arg.event.extendedProps.entry;
            return (
              <div className="flex items-center gap-1 px-1 py-0.5 overflow-hidden">
                {e.is_completed && <span className="text-[10px]">✓</span>}
                <span className={`text-[11px] truncate ${e.is_completed ? 'line-through opacity-80' : ''}`}>
                  {arg.event.title}
                </span>
                {e.attachment_count > 0 && <span className="text-[9px] opacity-80">📎</span>}
              </div>
            );
          }}
        />
        </div>
      </div>

      {modal && (
        <ContentEntryModal
          mode={modal.mode}
          entryId={modal.entryId}
          defaultDate={modal.defaultDate}
          staff={staff}
          onClose={() => setModal(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
};

export default MarketingCalendar;
